import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, formatTarih, formatTl } from '../lib/api';
import { useAuth } from '../lib/auth';

type Satir = {
  id: number; no: string; cari: string;
  status: string; statusLabel: string;
  totalKurus: number; satirAdedi: number; tavanAsan: number;
  onayBekliyor: boolean; onaylayan: string | null; hazirlayan: string | null;
  pdfVar: boolean; createdAt: string; updatedAt: string;
};
type Veri = {
  satirlar: Satir[];
  ozet: {
    buAyAdet: number; buAyTutarKurus: number; kazanilan: number;
    donusYuzde: number; onayBekleyen: number;
  };
};

const ROZET: Record<string, string> = {
  TASLAK: 'ccd-badge',
  GONDERILDI: 'ccd-badge ccd-badge-info',
  ONAYLANDI: 'ccd-badge ccd-badge-success',
  KAYBEDILDI: 'ccd-badge ccd-badge-danger',
};

const SUZGECLER = [
  { id: 'hepsi', ad: 'Tümü' },
  { id: 'TASLAK', ad: 'Taslak' },
  { id: 'GONDERILDI', ad: 'Gönderildi' },
  { id: 'ONAYLANDI', ad: 'Satışa döndü' },
  { id: 'KAYBEDILDI', ad: 'Kaybedildi' },
];

export default function Teklif() {
  const { can } = useAuth();
  const git = useNavigate();
  const [durum, setDurum] = useState('hepsi');
  const [ara, setAra] = useState('');
  const [veri, setVeri] = useState<Veri | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [aciliyor, setAciliyor] = useState(false);

  const yukle = useCallback(() => {
    const q = new URLSearchParams({ durum, ...(ara ? { ara } : {}) });
    api<Veri>(`/api/teklif?${q}`).then(setVeri).catch((e) => setHata((e as Error).message));
  }, [durum, ara]);
  useEffect(() => {
    const t = setTimeout(yukle, ara ? 250 : 0);
    return () => clearTimeout(t);
  }, [yukle, ara]);

  async function yeniTeklif() {
    setAciliyor(true);
    try {
      const r = await api<{ id: number }>('/api/teklif', { method: 'POST', body: JSON.stringify({}) });
      git(`/teklif/${r.id}`);
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'Teklif açılamadı');
      setAciliyor(false);
    }
  }

  if (hata && !veri) {
    return (
      <div className="ccd-banner ccd-banner-danger">
        <span className="ccd-banner-icon" aria-hidden="true">!</span><span>{hata}</span>
      </div>
    );
  }

  return (
    <>
      <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 className="ccd-title">Teklifler</h1>
          <div className="ccd-meta">
            Kod yapıştır, satırlar liste fiyatıyla gelsin · iskonto tavanı uyarır, engellemez
          </div>
        </div>
        {can('quote.write') && (
          <button className="ccd-btn ccd-btn-primary ccd-btn-sm" onClick={yeniTeklif} disabled={aciliyor} data-tur="yeni-teklif">
            {aciliyor ? 'Açılıyor…' : '+ Yeni Teklif'}
          </button>
        )}
      </div>

      {veri && (
        <div className="ccd-grid ccd-grid-3">
          <div className="ccd-stat">
            <div className="ccd-stat-label">Bu ay teklif</div>
            <div className="ccd-stat-value">{veri.ozet.buAyAdet}</div>
            <div className="ccd-stat-foot">
              <span className={veri.ozet.donusYuzde >= 30 ? 'ccd-kpi-up' : ''}>
                %{veri.ozet.donusYuzde} satışa döndü
              </span>{' '}
              · {formatTl(veri.ozet.buAyTutarKurus)} tutar
            </div>
          </div>
          <div className="ccd-stat">
            <div className="ccd-stat-label">Satışa dönen</div>
            <div className="ccd-stat-value">{veri.ozet.kazanilan}</div>
            <div className="ccd-stat-foot">bu ay onaylanan teklif</div>
          </div>
          <div className="ccd-stat">
            <div className="ccd-stat-label">Onay bekleyen</div>
            <div className="ccd-stat-value">{veri.ozet.onayBekleyen}</div>
            <div className="ccd-stat-foot">iskonto tavanı aşan teklif</div>
          </div>
        </div>
      )}

      <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div className="ccd-tabs" role="tablist">
          {SUZGECLER.map((s) => (
            <button key={s.id} role="tab" aria-selected={durum === s.id}
                    className="ccd-tab" onClick={() => setDurum(s.id)}>
              {s.ad}
            </button>
          ))}
        </div>
        <div className="ccd-search" style={{ minWidth: 240 }}>
          <span className="ccd-search-icon" aria-hidden="true">⌕</span>
          <input className="ccd-input" placeholder="Teklif no ya da cari ara…"
                 value={ara} onChange={(e) => setAra(e.target.value)} aria-label="Teklif ara" />
        </div>
      </div>

      {!veri ? (
        <div className="ccd-inline-note">Yükleniyor…</div>
      ) : veri.satirlar.length === 0 ? (
        <div className="ccd-empty">
          Bu süzgeçte teklif yok.{can('quote.write') && ' "+ Yeni Teklif" ile başlayın.'}
        </div>
      ) : (
        <div className="ccd-table-wrap">
          <table className="ccd-table">
            <thead>
              <tr>
                <th className="ccd-th">No</th>
                <th className="ccd-th">Cari</th>
                <th className="ccd-th">Durum</th>
                <th className="ccd-th ccd-td-num">Satır</th>
                <th className="ccd-th ccd-td-num">Genel toplam</th>
                <th className="ccd-th">Hazırlayan</th>
                <th className="ccd-th">Son değişiklik</th>
              </tr>
            </thead>
            <tbody>
              {veri.satirlar.map((t) => (
                <tr key={t.id} className="ccd-tr ccd-tr-clickable" onClick={() => git(`/teklif/${t.id}`)}>
                  <td className="ccd-td ccd-mono ccd-td-nowrap">{t.no}</td>
                  <td className="ccd-td">{t.cari}</td>
                  <td className="ccd-td ccd-td-nowrap">
                    <span className={ROZET[t.status] ?? 'ccd-badge'}>{t.statusLabel}</span>
                    {t.onayBekliyor && (
                      <div className="ccd-meta ccd-kpi-down">tavan aşıldı · yetkili onayı yok</div>
                    )}
                  </td>
                  <td className="ccd-td ccd-td-num">{t.satirAdedi}</td>
                  <td className="ccd-td ccd-td-num"><b>{formatTl(t.totalKurus)}</b></td>
                  <td className="ccd-td ccd-meta">{t.hazirlayan ?? '—'}</td>
                  <td className="ccd-td ccd-meta ccd-td-nowrap">{formatTarih(t.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
